import { useState, useEffect } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../utils/api'
import { Save, X, ChevronLeft, Package, Monitor, User } from 'lucide-react'

const EMPTY = {
  assetId: '', docNumber: '', serialNumber: '', registration: '',
  contractStart: '',
  contractEnd: '',
  employeeId: '', employeeName: '', department: '', location: '',
  os: '', standardPrograms: '', otherPrograms: '', brand: '', purchaseType: 'Purchase',
  cpu: '', vga: '', ram: '', hdd: '', monitor: '',
  docStatus: 'PENDING',
}

const DOC_STATUS_OPTS = [
  { value: 'PENDING',   label: 'รอการแนบไฟล์' },
  { value: 'COMPLETED', label: 'มีใบส่งมอบ' },
  { value: 'CANCELLED', label: 'ยกเลิกใบส่งมอบ' },
]

const Field = ({ label, children, span = false }: { label: string; children: React.ReactNode; span?: boolean }) => (
  <div className={span ? 'md:col-span-2' : ''}>
    <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
    {children}
  </div>
)

const ic = 'w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400'
const ta = ic + ' resize-none'

export default function DeliveryFormPage() {
  const { id } = useParams()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const isEdit = !!id && id !== 'new'

  const [form, setForm] = useState(EMPTY)
  const [assetSearch, setAssetSearch] = useState('')

  const { data: existingDoc } = useQuery({
    queryKey: ['delivery', id],
    queryFn: () => api.get(`/deliveries/${id}`).then(r => r.data),
    enabled: isEdit,
  })

  const { data: assets = [] } = useQuery<any[]>({
    queryKey: ['assets-for-delivery'],
    queryFn: () => api.get('/assets?limit=500&sortBy=assetCode&sortOrder=asc').then(r => r.data.assets),
  })

  // Fetch software linked to selected asset
  const { data: assetSoftware = [] } = useQuery<any[]>({
    queryKey: ['asset-software-delivery', form.assetId],
    queryFn: () => api.get(`/software/asset/${form.assetId}`).then(r => r.data),
    enabled: !!form.assetId,
  })

  const preAssetId    = searchParams.get('assetId')
  const preAssignedTo = searchParams.get('assignedTo')
  const preEmployeeId = searchParams.get('employeeId')
  const selectedAsset = assets.find((a: any) => a.id === form.assetId)

  useEffect(() => {
    if (isEdit && existingDoc) {
      setForm({
        assetId:          existingDoc.assetId          || '',
        docNumber:        existingDoc.docNumber         || '',
        serialNumber:     existingDoc.serialNumber      || '',
        registration:     existingDoc.registration      || '',
        contractStart:    existingDoc.contractStart ? existingDoc.contractStart.split('T')[0] : '',
        contractEnd:      existingDoc.contractEnd   ? existingDoc.contractEnd.split('T')[0]   : '',
        employeeId:       existingDoc.employeeId        || '',
        employeeName:     existingDoc.employeeName       || '',
        department:       existingDoc.department         || '',
        location:         existingDoc.location           || '',
        os:               existingDoc.os                 || '',
        standardPrograms: existingDoc.standardPrograms   || '',
        otherPrograms:    existingDoc.otherPrograms       || '',
        brand:            existingDoc.brand               || '',
        purchaseType:     existingDoc.purchaseType        || 'Purchase',
        docStatus:        existingDoc.docStatus           || 'PENDING',
        cpu:              existingDoc.cpu                 || '',
        vga:              existingDoc.vga                 || '',
        ram:              existingDoc.ram                 || '',
        hdd:              existingDoc.hdd                 || '',
        monitor:          existingDoc.monitor             || '',
      })
    }
  }, [isEdit, existingDoc])

  // Pre-fill from asset when assetId changes
  useEffect(() => {
    if (!form.assetId) return
    const asset = assets.find((a: any) => a.id === form.assetId)
    if (!asset) return
    const isComputer = ['LAPTOP', 'OTHER'].includes(asset.type)
    // purchaseDate from asset → contractStart
    const purchaseDateStr = asset.purchaseDate ? asset.purchaseDate.split('T')[0] : ''
    setForm(f => ({
      ...f,
      docNumber:    f.docNumber    || asset.assetCode    || '',
      serialNumber: f.serialNumber || asset.serialNumber || '',
      registration: f.registration || asset.assetTag     || '',
      brand:        f.brand        || asset.brand         || '',
      purchaseType: f.purchaseType || 'Purchase',
      employeeId:   f.employeeId   || asset.employeeId   || '',
      employeeName: f.employeeName || asset.assignedTo   || '',
      department:   f.department   || asset.department   || '',
      location:     f.location     || asset.site         || '',
      contractStart: f.contractStart || purchaseDateStr,  // วันที่จัดซื้อ
      ...(isComputer && {
        cpu:     f.cpu     || asset.cpu     || asset.spec || '',
        vga:     f.vga     || asset.vga     || '',
        ram:     f.ram     || asset.ram     || '',
        hdd:     f.hdd     || asset.hdd     || '',
        monitor: f.monitor || asset.monitorSpec  || '',
      }),
    }))
  }, [form.assetId, assets])

  // Pre-fill from URL param
  useEffect(() => {
    if (preAssetId && !isEdit && assets.length > 0) {
      setForm(f => ({
        ...f,
        assetId: preAssetId,
        ...(preAssignedTo && { employeeName: preAssignedTo }),
        ...(preEmployeeId && { employeeId: preEmployeeId }),
      }))
    }
  }, [preAssetId, assets, isEdit])

  const saveMut = useMutation({
    mutationFn: (data: typeof EMPTY) =>
      isEdit ? api.put(`/deliveries/${id}`, data) : api.post('/deliveries', data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['deliveries'] })
      navigate('/deliveries')
    },
  })

  const set = (k: keyof typeof EMPTY, v: string) => setForm(f => ({ ...f, [k]: v }))

  const filteredAssets = assets.filter((a: any) =>
    !assetSearch || [a.assetCode, a.name, a.serialNumber].some(v => v?.toLowerCase().includes(assetSearch.toLowerCase()))
  )

  const osSoftware      = assetSoftware.filter((s: any) => s.softwareCategory === 'OS')
  const generalSoftware = assetSoftware.filter((s: any) => s.softwareCategory !== 'OS')

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-5">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate('/deliveries')} className="p-2 hover:bg-gray-100 rounded-lg text-gray-500">
          <ChevronLeft size={18}/>
        </button>
        <h1 className="text-xl font-bold text-gray-900">{isEdit ? 'แก้ไขใบส่งมอบ' : 'สร้างใบส่งมอบ'}</h1>
      </div>

      <div className="space-y-5">
        {/* Link Asset */}
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
          <h2 className="font-semibold text-gray-700 mb-4 text-sm">เชื่อมโยง Asset</h2>
          <div className="space-y-2">
            <input value={assetSearch} onChange={e => setAssetSearch(e.target.value)}
              placeholder="ค้นหา asset code, ชื่อ, serial..."
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"/>
            <select value={form.assetId} onChange={e => set('assetId', e.target.value)} className={ic}>
              <option value="">— ไม่เชื่อมโยง Asset —</option>
              {filteredAssets.map((a: any) => (
                <option key={a.id} value={a.id}>
                  {a.assetCode ? `[${a.assetCode}] ` : ''}{a.name}{a.serialNumber ? ` | ${a.serialNumber}` : ''}
                </option>
              ))}
            </select>
            {selectedAsset && (
              <p className="text-xs text-blue-600 bg-blue-50 px-3 py-1.5 rounded-lg">
                เชื่อมกับ: {selectedAsset.name} {selectedAsset.brand ? `(${selectedAsset.brand})` : ''}
              </p>
            )}
          </div>
        </div>

        {/* Software linked to asset — read only display */}
        {form.assetId && (
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
            <h2 className="font-semibold text-gray-700 mb-3 text-sm flex items-center gap-2">
              <Package size={15} className="text-indigo-500"/> Software ที่ผูกกับ Asset ({assetSoftware.length} รายการ)
            </h2>
            {assetSoftware.length === 0 ? (
              <p className="text-xs text-gray-400 text-center py-3">ยังไม่มี Software ที่ผูกกับ Asset นี้
                <a href="/software" target="_blank" className="ml-1 text-blue-500 hover:underline">→ จัดการ Software</a>
              </p>
            ) : (
              <div className="space-y-3">
                {/* OS */}
                {osSoftware.length > 0 && (
                  <div>
                    <p className="text-xs font-semibold text-gray-400 mb-1.5 flex items-center gap-1"><Monitor size={11}/> ระบบปฏิบัติการ</p>
                    <div className="space-y-1">
                      {osSoftware.map((sw: any) => (
                        <div key={sw.id} className="flex items-center gap-2.5 p-2.5 bg-indigo-50 rounded-lg">
                          <span className="text-xs bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded font-medium flex-shrink-0">OS</span>
                          <span className="text-sm font-medium text-gray-800 flex-1">{sw.name}</span>
                          {sw.version && <span className="text-xs text-gray-400">v{sw.version}</span>}
                          {sw.licenseKey && <span className="text-xs font-mono text-gray-400 truncate max-w-32">{sw.licenseKey}</span>}
                          {sw.expiryDate && (
                            <span className={`text-xs flex-shrink-0 ${new Date(sw.expiryDate) < new Date() ? 'text-red-500' : 'text-gray-400'}`}>
                              หมด {new Date(sw.expiryDate).toLocaleDateString('th-TH')}
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {/* General Apps */}
                {generalSoftware.length > 0 && (
                  <div>
                    <p className="text-xs font-semibold text-gray-400 mb-1.5 flex items-center gap-1"><User size={11}/> โปรแกรมทั่วไป</p>
                    <div className="space-y-1">
                      {generalSoftware.map((sw: any) => (
                        <div key={sw.id} className="flex items-center gap-2.5 p-2.5 bg-gray-50 rounded-lg">
                          <span className="text-xs bg-gray-200 text-gray-600 px-2 py-0.5 rounded font-medium flex-shrink-0">App</span>
                          <span className="text-sm font-medium text-gray-800 flex-1">{sw.name}</span>
                          {sw.version && <span className="text-xs text-gray-400">v{sw.version}</span>}
                          {sw.licenseKey && <span className="text-xs font-mono text-gray-400 truncate max-w-32">{sw.licenseKey}</span>}
                          {sw.expiryDate && (
                            <span className={`text-xs flex-shrink-0 ${new Date(sw.expiryDate) < new Date() ? 'text-red-500' : 'text-gray-400'}`}>
                              หมด {new Date(sw.expiryDate).toLocaleDateString('th-TH')}
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Header Info */}
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
          <h2 className="font-semibold text-gray-700 mb-4 text-sm">ข้อมูลเครื่อง / เอกสาร</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="ชื่อเครื่อง / เลขที่เอกสาร">
              <input value={form.docNumber} onChange={e => set('docNumber', e.target.value)} placeholder="เช่น N0328" className={ic}/>
            </Field>
            <Field label="Serial Number">
              <input value={form.serialNumber} onChange={e => set('serialNumber', e.target.value)} placeholder="เช่น PF5JTK6D" className={ic}/>
            </Field>
            <Field label="ทะเบียนทรัพย์สิน (Asset Tag)">
              <input value={form.registration} onChange={e => set('registration', e.target.value)} placeholder="รหัสทรัพย์สิน" className={ic}/>
            </Field>
            <Field label="วันที่จัดซื้อ">
              <input type="date" value={form.contractStart} onChange={e => set('contractStart', e.target.value)} className={ic}/>
            </Field>
            <Field label="สถานะใบส่งมอบ">
              <select value={form.docStatus} onChange={e => set('docStatus', e.target.value)} className={ic}>
                {DOC_STATUS_OPTS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </Field>
          </div>
        </div>

        {/* Employee Info */}
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
          <h2 className="font-semibold text-gray-700 mb-4 text-sm">ข้อมูลพนักงาน</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="รหัสพนักงาน">
              <input value={form.employeeId} onChange={e => set('employeeId', e.target.value)} placeholder="เช่น 68051" className={ic}/>
            </Field>
            <Field label="ชื่อ - นามสกุล">
              <input value={form.employeeName} onChange={e => set('employeeName', e.target.value)} placeholder="ชื่อพนักงาน" className={ic}/>
            </Field>
            <Field label="หน่วยงาน">
              <input value={form.department} onChange={e => set('department', e.target.value)} placeholder="เช่น CC10904" className={ic}/>
            </Field>
            <Field label="สถานที่">
              <input value={form.location} onChange={e => set('location', e.target.value)} placeholder="เช่น CIG1" className={ic}/>
            </Field>
          </div>
        </div>

        {/* Model / Brand */}
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
          <h2 className="font-semibold text-gray-700 mb-4 text-sm">รายละเอียดเครื่อง</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Model / Brand">
              <input value={form.brand} onChange={e => set('brand', e.target.value)} placeholder="เช่น Lenovo" className={ic}/>
            </Field>
            <Field label="ประเภทการได้มา">
              <select value={form.purchaseType} onChange={e => set('purchaseType', e.target.value)} className={ic}>
                <option value="Purchase">Purchase</option>
                <option value="Lease">Lease</option>
                <option value="Rent">Rent</option>
              </select>
            </Field>
          </div>
        </div>

        {/* Hardware Specs */}
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
          <h2 className="font-semibold text-gray-700 mb-4 text-sm">รายการ Hardware</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {([['cpu','CPU'],['vga','VGA'],['ram','RAM'],['hdd','HDD'],['monitor','Monitor']] as [keyof typeof EMPTY, string][]).map(([k,label]) => (
              <Field key={k} label={label}>
                <input value={form[k] as string} onChange={e => set(k, e.target.value)} className={ic}/>
              </Field>
            ))}
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center justify-between pt-2">
        <button onClick={() => navigate('/deliveries')} className="flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-xl text-sm text-gray-600 hover:bg-gray-50">
          <X size={15}/> ยกเลิก
        </button>
        <button onClick={() => saveMut.mutate(form)}
          disabled={saveMut.isPending}
          className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-xl text-sm font-medium hover:bg-blue-700 disabled:opacity-50">
          <Save size={15}/> {saveMut.isPending ? 'กำลังบันทึก...' : 'บันทึก'}
        </button>
      </div>
    </div>
  )
}
